import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getSupabaseClient } from "../providers/supabase/supabase";

export type ApprovalQueueStatus = "pending" | "approved" | "rejected" | "needs_review";

export type ApprovalQueueItem = {
  id: string;
  queue_type: string;
  source_record_id: string | null;
  contact_id: number | null;
  company_id: number | null;
  deal_id: number | null;
  reason: string;
  priority: string | null;
  status: ApprovalQueueStatus;
  resolution_payload: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

const QUERY_KEY = ["approval-queue"];

/**
 * mentorax.approval_queue is in the mentorax schema, not public — the
 * react-admin data provider is scoped to public, so this queries the
 * Supabase client directly, same pattern as useContactOutreachTimeline.ts.
 * DR-style grant applied 2026-09-04: authenticated gets SELECT (open) +
 * UPDATE (gated by is_crm_user()), no INSERT/DELETE — agents write via
 * service_role, humans only ever resolve, never delete a row.
 */
export function useApprovalQueue() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<ApprovalQueueItem[]> => {
      const { data, error } = await getSupabaseClient()
        .schema("mentorax")
        .from("approval_queue")
        .select("*")
        .in("status", ["pending", "needs_review"])
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ApprovalQueueItem[];
    },
  });
}

/**
 * Resolves a queue item per agent-crm-integration-decisions.md Q3: adds a
 * `resolution` key into resolution_payload rather than mutating `proposal`,
 * so the row keeps a clean before/after audit trail. Status flips to
 * approved/rejected — "edited then approved" still just means status =
 * approved, distinguished by resolution.action if a future edit UI is added.
 */
export function useResolveApprovalItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      item,
      action,
      resolvedBy,
    }: {
      item: ApprovalQueueItem;
      action: "approved" | "rejected";
      resolvedBy: string;
    }) => {
      const { error } = await getSupabaseClient()
        .schema("mentorax")
        .from("approval_queue")
        .update({
          status: action,
          resolution_payload: {
            ...item.resolution_payload,
            resolution: {
              action,
              resolved_by: resolvedBy,
              resolved_at: new Date().toISOString(),
            },
          },
        })
        .eq("id", item.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
